/**
 * Account Detail Page
 *
 * Matches Client detail: header then tabs (Overview, Transactions, Activity, Cases, Products, Files).
 * Overview holds type, owner, description; firm: pricebook + related contacts; individual: phone, email, billing.
 * Note: Tasks and History tabs removed - activities consolidated into Activity Timeline (PE-1458)
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  useAccount,
  useContactsByAccount,
  usePricebookByAccount,
  useDeleteAccount,
} from '../../services/queries/accountQueries';
import { useInvoicesByAccount } from '../../services/queries/invoiceQueries';
import { persistedPricebookRecords } from '../../services/mockData/pricebooks';
import { DetailPageLayout } from '../../components/detail/DetailPageLayout';
import { DetailHeader } from '../../components/detail/DetailHeader';
import { DetailTabs } from '../../components/detail/DetailTabs';
import { ActivityTab } from '../../components/detail/ActivityTab';
import { CasesTab } from '../../components/detail/CasesTab';
import { AccountModal } from '../../components/modals/AccountModal';
import { LogCallModal } from '../../components/modals/LogCallModal';
import { LogMeetingModal } from '../../components/modals/LogMeetingModal';
import { LogTaskModal } from '../../components/modals/LogTaskModal';
import { AddNoteModal } from '../../components/modals/AddNoteModal';
import { SendEmailModal } from '../../components/modals/SendEmailModal';
import { useToast } from '../../contexts/ToastContext';
import {
  User,
  Briefcase,
  Package,
  Paperclip,
  Plus,
  Upload,
  Clock,
  Users,
  Receipt,
} from 'lucide-react';

interface AccountDetailPageProps {
  id: string;
  onBack: () => void;
}

export const AccountDetailPage: React.FC<AccountDetailPageProps> = ({ id, onBack }) => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const { data: account, isLoading } = useAccount(id);
  const { data: pricebookInfo } = usePricebookByAccount(account?.type === 'firm' ? id : undefined);
  const { data: contacts } = useContactsByAccount(account?.type === 'firm' ? id : undefined);
  const { data: transactions = [], isLoading: transactionsLoading } = useInvoicesByAccount(id);
  const deleteAccount = useDeleteAccount();

  const invoiceStatusColors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    overdue: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };

  const managingPartnerId =
    account?.type === 'firm' && pricebookInfo?.firmPricebook
      ? persistedPricebookRecords.get().find((pb) => pb.accountId === id)?.partnerId
      : undefined;

  const handleDelete = () => {
    if (!account) return;
    if (!window.confirm(`Are you sure you want to delete ${account.name}?`)) return;
    deleteAccount.mutate(id, {
      onSuccess: () => {
        success('Account deleted', `${account.name} has been deleted`);
        onBack();
      },
      onError: (err: Error) => {
        showError('Cannot delete account', err.message);
      },
    });
  };

  if (isLoading) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Accounts">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </DetailPageLayout>
    );
  }

  if (!account) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Accounts">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Account not found</p>
        </div>
      </DetailPageLayout>
    );
  }

  const isFirm = account.type === 'firm';
  const subtitle = isFirm ? `Firm • Owned by ${account.accountOwner || '—'}` : `Person Account • Owned by ${account.accountOwner || '—'}`;

  // PE-1458: Tasks and History tabs removed - activities consolidated into Activity Timeline
  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'transactions', label: 'Transactions', icon: Receipt },
    { id: 'activity', label: 'Activity', icon: Clock },
    { id: 'cases', label: 'Cases', icon: Briefcase },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'files', label: 'Files', icon: Paperclip },
  ];

  return (
    <DetailPageLayout onBack={onBack} backLabel="Back to Accounts">
      <div className="max-w-6xl mx-auto space-y-6">
        <DetailHeader
          title={account.name}
          subtitle={subtitle}
          recordId={account.id}
          onEdit={() => setIsEditModalOpen(true)}
          onDelete={handleDelete}
        />

        <AccountModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          account={account}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['account', id] });
            queryClient.invalidateQueries({ queryKey: ['accounts'] });
            queryClient.invalidateQueries({ queryKey: ['accountListViewData'] });
          }}
        />

        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DetailTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Type</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isFirm ? 'Firm' : 'Individual'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Account Owner</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {account.accountOwner || '—'}
                    </p>
                  </div>
                  {isFirm && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Current Price Book</p>
                      {pricebookInfo?.displayPricebook ? (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/pricebooks/${pricebookInfo.displayPricebook!.id}`)
                          }
                          className="text-lg font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {pricebookInfo.displayPricebook.name}
                        </button>
                      ) : (
                        <p className="text-lg font-semibold text-gray-500 dark:text-gray-400">—</p>
                      )}
                    </div>
                  )}
                  {!isFirm && (
                    <>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Phone</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {account.phone || '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Email</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {account.email || '—'}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {account.description && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Description
                    </h3>
                    <p className="text-gray-900 dark:text-white">{account.description}</p>
                  </div>
                )}

                {!isFirm && account.billingAddress && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Billing Address
                    </h3>
                    <p className="text-gray-900 dark:text-white">{account.billingAddress}</p>
                  </div>
                )}

                {isFirm && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Related Contacts
                    </h3>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-[#334155] border-b border-gray-200 dark:border-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              Title
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              Role
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {(contacts || []).map(
                            (c: {
                              id: string;
                              name?: string;
                              title?: string;
                              email?: string;
                            }) => (
                              <tr
                                key={c.id}
                                className="hover:bg-gray-50 dark:hover:bg-[#334155]"
                              >
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                  {c.name ?? c.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {c.title ?? '—'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {c.email ?? '—'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {c.id === managingPartnerId ? (
                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                                      Managing partner
                                    </span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                      {(!contacts || contacts.length === 0) && (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                          <p>No related contacts</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'transactions' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Transactions
                </h3>
                {transactionsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                    <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">No transactions</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                      Invoices for clients linked to this account will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-[#334155] border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Invoice Number
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Client
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Issue Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Due Date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {transactions.map((inv) => (
                          <tr key={inv.id} className="hover:bg-gray-50 dark:hover:bg-[#334155]">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Link
                                to={`/invoices/${inv.id}`}
                                className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                {inv.invoiceNumber}
                              </Link>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {inv.clientName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  invoiceStatusColors[inv.status] ?? invoiceStatusColors.draft
                                }`}
                              >
                                {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: inv.currency,
                              }).format(inv.totalAmount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(inv.issueDate).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(inv.dueDate).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'activity' && (
              <ActivityTab
                recordId={account.id}
                recordType="account"
                onLogActivity={(type) => {
                  if (type === 'call') setShowCallModal(true);
                  if (type === 'meeting') setShowMeetingModal(true);
                  if (type === 'task') setShowTaskModal(true);
                  if (type === 'note') setShowNoteModal(true);
                }}
              />
            )}

            {activeTab === 'cases' && (
              <CasesTab accountId={account.id} accountName={account.name} />
            )}

            {activeTab === 'products' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Products & Services
                  </h3>
                  <button className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    <Plus className="w-4 h-4" />
                    Add Product
                  </button>
                </div>
                <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                    Enrolled Products
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No products enrolled for this account.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'files' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Files & Documents
                  </h3>
                  <button className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    <Upload className="w-4 h-4" />
                    Upload File
                  </button>
                </div>
                <div className="bg-white dark:bg-[#1e293b] rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 p-12">
                  <div className="text-center">
                    <Paperclip className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No files uploaded
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Upload documents for this account.
                    </p>
                    <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      Upload Files
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      <LogCallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        recordId={account.id}
        recordType="account"
        contactName={account.name}
      />
      <LogMeetingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        recordId={account.id}
        recordType="account"
      />
      <LogTaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        recordId={account.id}
        recordType="account"
        contactName={account.name}
      />
      <AddNoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        recordId={account.id}
        recordType="account"
      />
      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        recordId={account.id}
        recordType="account"
        recipientEmail={account.email ?? ''}
        recipientName={account.name}
      />
    </DetailPageLayout>
  );
};
